import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ReceiptManagement.css';
import ReceiptSidebar from './ReceiptSidebar';
import ReceiptDashboardView from './ReceiptDashboardView';
import ReceiptSubmitView from './ReceiptSubmitView';
import StaffRegistryView from './StaffRegistryView';
import { appName, orgName } from '../brand';

function ReceiptManagement({ preview = false }) {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('dashboard');
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  // Check if admin is already logged in
  useEffect(() => {
    const adminStatus = sessionStorage.getItem('receipt_admin_logged_in');
    if (adminStatus === 'true') {
      setIsAdmin(true);
    }
  }, []);

  const handleAdminLogin = (e) => {
    e.preventDefault();
    // Simple password check (in production, this should be a proper backend API call)
    if (adminPassword === 'admin123') {
      setIsAdmin(true);
      sessionStorage.setItem('receipt_admin_logged_in', 'true');
      setShowAdminLogin(false);
      setAdminPassword('');
      setLoginError('');
    } else {
      setLoginError('Invalid password');
    }
  };

  const handleAdminLogout = () => {
    setIsAdmin(false);
    sessionStorage.removeItem('receipt_admin_logged_in');
  };

  const renderView = () => {
    switch (activeView) {
      case 'dashboard':
        return <ReceiptDashboardView isAdmin={isAdmin} />;
      case 'submit':
        return <ReceiptSubmitView />;
      case 'staff':
        return <StaffRegistryView isAdmin={isAdmin} />;
      default:
        return <ReceiptDashboardView isAdmin={isAdmin} />;
    }
  };

  return (
    <div className="App">
      {/* Preview-only banner — this view is not linked anywhere in the app and is
          reached by manually navigating to the secret /receipts-preview path. */}
      {preview && (
        <div
          style={{
            background: 'linear-gradient(90deg, var(--brand-accent, #7c3aed), var(--brand-primary, #863698))',
            color: '#fff',
            padding: '10px 20px',
            textAlign: 'center',
            fontWeight: 600,
            fontSize: '0.9rem',
            letterSpacing: '0.01em',
          }}
        >
          🔎 Preview only — the Receipt Manager is not part of the current product.
          This page is an internal preview and is intentionally not shown in the navigation.
        </div>
      )}

      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo">{appName()}{preview ? ' · Receipts Preview' : ''}</div>
          </div>
          <nav className="nav-menu">
            <button className="nav-item" onClick={() => navigate('/')}>Dashboard</button>
            <button className="nav-item" onClick={() => navigate('/individuals')}>Support Plans</button>
            <button className="nav-item" onClick={() => navigate('/import')}>Import</button>
            <button className="nav-item active" onClick={() => navigate(preview ? '/receipts-preview' : '/receipts')}>Receipts</button>
            {isAdmin ? (
              <button className="nav-item admin-btn" onClick={handleAdminLogout} title="Logout as Admin">
                🔓 Admin
              </button>
            ) : (
              <button className="nav-item" onClick={() => setShowAdminLogin(true)} title="Login as Admin">
                🔒
              </button>
            )}
            <button className="nav-item profile-btn">👤</button>
          </nav>
        </div>
      </header>

      {/* Main Layout with Sidebar */}
      <div className="receipt-management-layout">
        <ReceiptSidebar activeView={activeView} onViewChange={setActiveView} />
        
        <main className="receipt-main-content">
          {renderView()}
        </main>
      </div>

      {/* Admin Login Modal */}
      {showAdminLogin && (
        <div className="modal-overlay" onClick={() => setShowAdminLogin(false)}>
          <div className="modal-content admin-login-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔒 Admin Login</h2>
              <button className="modal-close" onClick={() => setShowAdminLogin(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleAdminLogin} className="admin-login-form">
              <div className="form-group">
                <label htmlFor="admin-password">Password</label>
                <input
                  type="password"
                  id="admin-password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="Enter admin password"
                  autoFocus
                  required
                />
                {loginError && (
                  <div className="login-error">⚠️ {loginError}</div>
                )}
                <small className="form-hint">
                  Admin access allows you to delete receipts and edit staff members
                </small>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowAdminLogin(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  🔓 Login
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-text">
            <span>© 2026 {orgName()} — {appName()}</span>
          </div>
          <div className="footer-links">
            <a href="mailto:thomas@strategylab.ca">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default ReceiptManagement;
